import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import useClaimRedirect from 'module/claims/hooks/useClaimRedirect';
import React, { useState } from 'react';
import { useClaimProvider } from '../Claim.Hooks';
import { ClaimJobListInline } from './Claim.ClaimJob.List';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ClaimList() {
  const { lang } = getLocales();
  const {
    listApi,
    pageRouter,
    statusInfo,
    emailTemplates,
    productsApi,
    hasAnyPermission,
    selectedClaims,
    setSelectedClaims,
    canChangeClaimOwnership,
    getAllSelectedClaims,
    hasUnchangeableSelectedClaims,
    areAllVisibleClaimsSelected,
  } = useClaimProvider();
  const { finalRedirect } = useClaimRedirect({ emailTemplates, statusInfo });
  const [expanedClaimId, setExpandedClaimId] = useState<number>();

  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.claims}
      render={lang.noInfo}
    />
  ) : (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>
            <Button
              class={ThemeButtonCircle}
              media={
                areAllVisibleClaimsSelected
                  ? 'fas-check-square'
                  : selectedClaims?.length
                    ? 'fas-minus-square'
                    : 'far-square'
              }
              disabled={!listApi?.payload?.count}
              onClick={async () => setSelectedClaims(await getAllSelectedClaims())}
              active={!!selectedClaims?.length}
              htmlElementProps={{
                title: hasUnchangeableSelectedClaims ? 'Some selected claims cannot change owner' : lang.selectAll,
              }}
            />
          </th>
          <th>
            {lang.claim}
            <SortButton orderBy="number" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th>{lang.info}</th>
          <th>
            {lang.incidentLocalDate}
            <SortButton orderBy="incidentLocalDate" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th>
            {lang.createdAt}
            <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th>
            {lang.lastModified}
            <SortButton orderBy="modifiedAtUtc" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th>
            {lang.status}
            <SortButton orderBy="statusCode" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th>
            {lang.owner}
            <SortButton orderBy="owner.firstName" thenBy="owner.lastName" apiBody={listApi.body} onSort={handleSort} />
          </th>
          <th data-actions>{lang.actions}</th>
        </tr>
      </thead>
      <tbody>
        <DataMap
          data={listApi.payload?.data}
          render={({ data: claim }) => {
            const isNew = pageRouter.query.newClaimId === claim.id;
            const currentClaim = selectedClaims?.find((it) => it.id === claim.id);
            const canSelectClaim = canChangeClaimOwnership(claim);
            return (
              <React.Fragment key={claim.id}>
                <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                  <td>
                    <Button
                      onClick={() => {
                        if (currentClaim) {
                          setSelectedClaims((prev) => prev?.filter((it) => it.id !== claim.id));
                        } else {
                          setSelectedClaims((prev) => [...(prev || []), claim]);
                        }
                      }}
                      disabled={!canSelectClaim}
                      class={ThemeButtonCircle}
                      media={<Icon class={currentClaim ? 'fas-check-square' : 'far-square'} />}
                      htmlElementProps={{
                        title: !canSelectClaim
                          ? (lang.missingPermission ?? 'Claim cannot be selected for owner change')
                          : currentClaim
                            ? lang.unselect
                            : lang.select,
                      }}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: Theme.Size.XS, alignItems: 'center' }}>
                      {claim?.claimJobs?.some((it) => it.areAutomationRulesApplied) && (
                        <Box
                          class={ThemeTagRounded}
                          style={{
                            backgroundColor: Theme.Color.success,
                            fontSize: Theme.FontSize.S,
                          }}
                        >
                          A
                        </Box>
                      )}
                      <Box
                        class={ThemeTagRounded}
                        style={{
                          backgroundColor: claim.claimJobCount ? null : Theme.Color.error,
                          fontSize: Theme.FontSize.S,
                        }}
                      >
                        {claim.claimJobCount > 1
                          ? `${claim.claimJobCount} ${lang.jobs}`
                          : claim.claimJobCount === 1
                            ? `1 ${lang.job}`
                            : lang.noJobs}
                      </Box>
                    </div>
                    <TextBox class={textBoxStyles.layout.title} style={{ fontWeight: 'bold' }}>
                      {claim.number ??
                        combineStrings(
                          '-',
                          productsApi?.payload?.data?.find(
                            (it) => it.id === (claim?.contract?.product?.id || claim?.contract?.productId)
                          )?.category?.code,
                          claim.id.toString()
                        )}
                    </TextBox>
                    <br />
                    <TextBox class={textBoxStyles.layout.infoLabel}>
                      {claim.contract?.product?.name ?? lang.unknownProduct}
                    </TextBox>
                  </td>
                  <td>
                    {lang.contact}: <b>{claim.createdByPayee?.name || 'N/A'}</b>
                    <br />
                    {lang.client}: <b>{claim.contract?.client?.name ?? 'N/A'}</b>
                  </td>
                  <td>
                    {claim?.incidentLocalDate ? localizeDate(claim?.incidentLocalDate, defaultDateTimeFormat) : 'N/A'}
                  </td>
                  <td>
                    {claim.reporter?.fullName && (
                      <>
                        {claim.reporter.fullName} {lang.at}
                        <br />
                      </>
                    )}
                    {localizeDate(claim?.createdAtUtc, defaultDateTimeFormat)}
                    {isNew && (
                      <span
                        style={{
                          backgroundColor: Theme.Color.success,
                          fontSize: Theme.FontSize.S,
                          fontWeight: 'bold',
                          borderRadius: '100px',
                          color: 'white',
                          padding: '3px 6px',
                          marginTop: Theme.Size.XS,
                        }}
                      >
                        {lang.newlyCreated.toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td>{localizeDate(claim?.modifiedAtUtc, defaultDateTimeFormat)}</td>
                  <td style={{ fontSize: Theme.FontSize.S }}>
                    <b>{lang[claim.statusCode]?.replace(/_/g, ' - ') || lang.noStatusDefined}</b>
                  </td>
                  <td>{claim.owner ? claim.owner?.fullName : 'N / A'}</td>
                  <td>
                    <Box class={ThemeWrapperStack}>
                      <Button
                        onClick={finalRedirect}
                        data={claim}
                        class={ThemeButtonCircle}
                        disabled={!hasAnyPermission}
                        active
                        media="fas-folder-open"
                        htmlElementProps={{
                          title: `${lang.open} ${lang.claim}`,
                        }}
                      />
                      <Button
                        onClick={setExpandedClaimId}
                        data={expanedClaimId === claim.id ? null : claim.id}
                        class={ThemeButtonCircle}
                        disabled={!hasAnyPermission || !claim.claimJobCount}
                        active
                        media={<Icon class={expanedClaimId === claim.id ? 'fas-chevron-up' : 'fas-chevron-down'} />}
                        htmlElementProps={{
                          title: expanedClaimId === claim.id ? lang.viewLess : lang.viewMore,
                        }}
                      />
                    </Box>
                  </td>
                </tr>
                {expanedClaimId === claim.id && <ClaimJobListInline claim={claim} />}
              </React.Fragment>
            );
          }}
        />
      </tbody>
    </table>
  );
}
